import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Loader2, Radio, CheckCircle2, Rocket, Megaphone, Search as SearchIcon, Share2, MonitorPlay } from "lucide-react";
import { sr } from "../../lib/sr";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const CH_ICON = { "Google Search": SearchIcon, Meta: Megaphone, Social: Share2, Display: MonitorPlay };

const Roll = ({ label, value, highlight }) => (
  <div style={highlight ? { background: "#F1FBF4", borderRadius: 10, padding: "6px 10px" } : {}}>
    <div className="sr-muted" style={{ fontSize: 11, fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 17, fontWeight: 700, color: highlight ? "#067647" : "var(--navy)", marginTop: 2 }}>{value}</div>
  </div>
);

function PackageCard({ pkg, onLaunch, busyKey }) {
  const [budget, setBudget] = useState(String(pkg.total_budget));
  const b = pkg.breakdown || {};
  const launched = !!pkg.launched_at;
  const busy = busyKey === pkg.key;
  return (
    <div className="sr-card" data-testid={`funasia-package-${pkg.key}`} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
        <div>
          <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: 18 }}>{pkg.name}</div>
          <div className="sr-muted" style={{ fontSize: 13, marginTop: 2 }}>{pkg.tagline}</div>
        </div>
        {launched && <span className="sr-badge sr-badge-green" data-testid={`funasia-launched-${pkg.key}`}><CheckCircle2 size={12} /> Launched</span>}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <span className="sr-badge" style={{ background: "#FBEFD9", color: "#9A7412" }}><Radio size={12} /> Radio {b.radio_pct}%</span>
        {(b.digital || []).map((d) => {
          const Ico = CH_ICON[d.channel] || Megaphone;
          return <span key={d.channel} className="sr-badge sr-badge-gray"><Ico size={12} /> {d.channel} {d.budget_pct}%</span>;
        })}
      </div>

      <ul style={{ margin: "2px 0 0", paddingLeft: 18, color: "var(--navy)", fontSize: 13, lineHeight: 1.7 }}>
        {(pkg.highlights || []).map((h, i) => <li key={i}>{h}</li>)}
      </ul>

      <div style={{ background: "var(--blue-light)", borderRadius: 10, padding: "10px 12px", fontSize: 13 }} data-testid={`funasia-breakdown-${pkg.key}`}>
        <div style={{ color: "var(--navy)", fontWeight: 700, marginBottom: 4 }}>What you get for {money(budget)}/mo</div>
        <div className="sr-muted">Radio flight: <b style={{ color: "var(--navy)" }}>{money(Math.round((Number(budget) * (b.radio_pct || 0)) / 100))}</b>
          {(b.digital || []).map((d) => (
            <span key={d.channel}> · {d.channel}: <b style={{ color: "var(--navy)" }}>{money(Math.round((Number(budget) * d.budget_pct) / 100))}</b></span>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: "auto" }}>
        <div style={{ display: "grid", gap: 2 }}>
          <label className="sr-muted" style={{ fontSize: 11 }}>Monthly budget</label>
          <input className="sr-input" type="number" style={{ width: 120 }} value={budget} onChange={(e) => setBudget(e.target.value)} data-testid={`funasia-budget-${pkg.key}`} />
        </div>
        <button className="sr-btn sr-btn-primary" disabled={busy} onClick={() => onLaunch(pkg.key, Number(budget))} data-testid={`funasia-launch-${pkg.key}`} style={{ alignSelf: "flex-end" }}>
          {busy ? <Loader2 size={15} className="sr-spin" /> : <Rocket size={15} />} {launched ? "Launch again" : "Launch package"}
        </button>
      </div>
    </div>
  );
}

export default function FunAsia() {
  const [data, setData] = useState(null);
  const [launches, setLaunches] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const load = () => {
    sr.funasiaPackages().then(setData).catch(() => setData({ error: true }));
    sr.funasiaLaunches().then((d) => setLaunches(d.launches || [])).catch(() => setLaunches([]));
  };
  useEffect(() => { load(); }, []);

  const launch = async (key, budget) => {
    if (!(budget > 0)) { toast.error("Set a monthly budget"); return; }
    setBusyKey(key);
    try {
      const r = await sr.launchFunasiaPackage(key, { budget_monthly: budget });
      toast.success(`Launched — 1 radio flight + ${r.campaigns.length} digital campaigns queued for setup`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not launch package"); }
    finally { setBusyKey(null); }
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load FunAsia packages. Please try again.</p></div>;

  return (
    <div data-testid="funasia-page">
      <div style={{ marginBottom: 6 }}>
        <span className="sr-badge" style={{ marginBottom: 8 }}><Package size={12} /> FunAsia Packages</span>
        <h1 style={{ fontSize: 30 }}>Ready-to-buy FunAsia packages</h1>
        <p className="sr-muted" style={{ marginTop: 6 }}>Bundles that pair FunAsia radio with digital in one launch. Pick a package, set your budget, and we set up the radio flight and every digital campaign for you.</p>
      </div>

      {(launches || []).length > 0 && (
        <div className="sr-card" style={{ marginTop: 16 }} data-testid="funasia-launches">
          <div className="sr-eyebrow" style={{ marginBottom: 4 }}>Your launched packages</div>
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 12 }}>Radio + digital performance for each package you've launched, rolled up in one view.</p>
          <div style={{ display: "grid", gap: 12 }}>
            {launches.map((L) => (
              <div key={L.key} className="sr-card" data-testid={`launch-${L.key}`} style={{ background: "#fff" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontWeight: 800, color: "var(--navy)" }}>{L.name}</div>
                    <div className="sr-muted" style={{ fontSize: 12, marginTop: 2 }}>{(L.channels || []).join(" · ")}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {!L.totals?.has_performance
                      ? <span className="sr-badge sr-badge-amber" data-testid={`launch-awaiting-${L.key}`}>Awaiting performance data</span>
                      : <span className="sr-badge sr-badge-green">Reporting</span>}
                    {L.benchmark && L.benchmark.rating !== "no_data" && (
                      <span className={`sr-badge ${L.benchmark.rating === "watch" ? "sr-badge-amber" : "sr-badge-green"}`} data-testid={`launch-benchmark-${L.key}`}>
                        {L.benchmark.rating === "best" ? "★ Best value" : L.benchmark.rating === "good" ? "Better than avg" : "Above avg cost"}
                        {L.benchmark.delta_pct != null && L.benchmark.rating !== "best" ? ` (${L.benchmark.delta_pct > 0 ? "+" : ""}${L.benchmark.delta_pct}%)` : ""}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 10, marginTop: 12 }} data-testid={`launch-totals-${L.key}`}>
                  <Roll label="Total budget" value={money(L.totals?.budget)} />
                  <Roll label="Spend" value={money(L.totals?.spend)} />
                  <Roll label="Clicks" value={Number(L.totals?.clicks || 0).toLocaleString()} />
                  <Roll label="Conversions" value={Number(L.totals?.conversions || 0).toLocaleString()} />
                  <Roll label="Digital cost / result" value={L.totals?.cpa != null ? money(L.totals.cpa) : "—"} highlight />
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 10, fontSize: 12 }}>
                  <span className="sr-badge" style={{ background: "#FBEFD9", color: "#9A7412" }}><Radio size={11} /> Radio: {L.radio?.flights || 0} flight{(L.radio?.flights === 1) ? "" : "s"} · {money(L.radio?.budget)} · {L.radio?.spots || 0} spots{L.radio?.spots_aired ? ` · ${Number(L.radio.spots_aired).toLocaleString()} aired` : ""}{L.radio?.reach ? ` · reach ${Number(L.radio.reach).toLocaleString()}` : ""}{(L.radio?.stations || []).length ? ` · ${L.radio.stations.join(", ")}` : ""}</span>
                  <span className="sr-badge sr-badge-gray"><Megaphone size={11} /> Digital: {L.digital?.campaigns || 0} campaigns · {money(L.digital?.budget)} · {money(L.digital?.spend)} spent</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 16, marginTop: 16 }} data-testid="funasia-packages">
        {(data.packages || []).map((p) => <PackageCard key={p.key} pkg={p} onLaunch={launch} busyKey={busyKey} />)}
      </div>

      <p className="sr-muted" style={{ fontSize: 12, marginTop: 18 }}>{data.note}</p>
    </div>
  );
}
